import React, {useRef, useState} from "react";
import {UploadIcon} from "lucide-react";
import Modal from "../../../../../components/Modal";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";

export type SponsorTier = "TITLE" | "GOLD" | "SILVER" | "BRONZE" | "PARTNER";

/** One row as the backend wants it. `website` may be null: plenty of sponsors have no site. */
type ImportRow = {
    name: string;
    image_url: string;
    website: string | null;
};

type ImportOutcome = "CREATED" | "SKIPPED_DUPLICATE" | "FAILED";

type ImportResultRow = {
    row: number;
    name: string;
    outcome: ImportOutcome;
    message: string | null;
};

type ImportResult = {
    created: number;
    skipped: number;
    failed: number;
    rows: ImportResultRow[];
};

/** Matches SponsorImportRequest.MAX_ROWS, so an over-long file is caught before the round trip. */
const MAX_ROWS = 100;

/** The file is a list of names and URLs. Anything this big is not that. */
const MAX_FILE_MB = 2;

/**
 * Turns the pasted or picked text into rows the backend accepts.
 *
 * <p>All-or-nothing, like `parseTicketImport` in TicketsStep: a file with a bad row comes back as
 * an error naming the offending 1-based rows rather than a partly-parsed list, so the administrator
 * fixes the file rather than discovering the gap afterwards. Per-row *import* failures are a
 * different thing and are reported by the server.
 */
export function parseSponsorImport(text: string): { items?: ImportRow[]; error?: string } {
    const trimmed = (text ?? "").trim();
    if (!trimmed) return {error: "Paste a JSON array of sponsors, or pick a file."};

    let data: unknown;
    try {
        data = JSON.parse(trimmed);
    } catch (e: any) {
        return {error: `Invalid JSON: ${e?.message ?? "could not parse"}`};
    }

    if (!Array.isArray(data)) {
        return {
            error: 'Expected a JSON array of sponsors, e.g. [ { "name": "Standard Bank", "image_url": "https://…/logo.png" } ].',
        };
    }
    if (data.length === 0) return {error: "The array is empty, so there is nothing to import."};
    if (data.length > MAX_ROWS) return {error: `That is ${data.length} sponsors. Import at most ${MAX_ROWS} at a time.`};

    const str = (v: unknown) => (typeof v === "string" ? v.trim() : "");

    const problems: string[] = [];
    data.forEach((row: any, i) => {
        const n = i + 1;
        if (row == null || typeof row !== "object" || Array.isArray(row)) {
            problems.push(`row ${n} (not an object)`);
            return;
        }
        if (!str(row.name)) problems.push(`row ${n} (missing "name")`);
        if (!str(row.image_url) && !str(row.imageUrl) && !str(row.logoUrl)) {
            problems.push(`row ${n} (missing "image_url")`);
        }
    });

    if (problems.length) return {error: `Fix these before importing: ${problems.join(", ")}.`};

    const items: ImportRow[] = data.map((row: any) => ({
        name: str(row.name),
        image_url: str(row.image_url) || str(row.imageUrl) || str(row.logoUrl),
        website: str(row.website) || str(row.websiteUrl) || null,
    }));

    return {items};
}

const TIERS: Array<{ value: SponsorTier; label: string }> = [
    {value: "TITLE", label: "Title"},
    {value: "GOLD", label: "Gold"},
    {value: "SILVER", label: "Silver"},
    {value: "BRONZE", label: "Bronze"},
    {value: "PARTNER", label: "Partner"},
];

/**
 * Bulk-creates an event's sponsors from a JSON file. Administrator only, and the caller is
 * responsible for that gate: this component does not check roles.
 *
 * <p>The logos are fetched and re-hosted server-side, which is why this posts URLs rather than
 * uploading files. A browser cannot read the organiser's site for us, and hotlinking their images
 * is not what we want stored either.
 */
export default function SponsorImportModal({
                                               open,
                                               onClose,
                                               eventId,
                                               onImported,
                                           }: {
    open: boolean;
    onClose: () => void;
    eventId: string;
    /** Called after any import that created something, so the caller can refresh its list. */
    onImported: () => void | Promise<void>;
}) {
    const [tier, setTier] = useState<SponsorTier>("PARTNER");
    const [text, setText] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<ImportResult | null>(null);
    const [importing, setImporting] = useState(false);

    const fileRef = useRef<HTMLInputElement | null>(null);

    const reset = () => {
        setText("");
        setError(null);
        setResult(null);
    };

    const close = () => {
        if (importing) return;
        reset();
        onClose();
    };

    const pickFile = (file: File | null) => {
        if (!file) return;
        setResult(null);

        if (file.size > MAX_FILE_MB * 1024 * 1024) {
            setError(`That file is larger than ${MAX_FILE_MB}MB, which a sponsor list should never be.`);
            return;
        }

        const reader = new FileReader();
        reader.onerror = () => setError("Could not read that file.");
        reader.onload = () => {
            const contents = typeof reader.result === "string" ? reader.result : "";
            setText(contents);
            // Surface a broken file immediately rather than on submit.
            setError(parseSponsorImport(contents).error ?? null);
        };
        reader.readAsText(file);
    };

    const runImport = async (e: React.FormEvent) => {
        e.preventDefault();
        if (importing) return;

        const {items, error: parseError} = parseSponsorImport(text);
        if (parseError || !items) {
            setError(parseError ?? "Nothing to import.");
            return;
        }
        setError(null);
        setResult(null);
        setImporting(true);

        try {
            const {data} = await api.post(
                `/api/v2/admin/events/${eventId}/sponsors/import`,
                {tier, sponsors: items},
                // The server fetches every logo from the organiser's host before answering. That is
                // a few seconds for a normal roster, but well past the client's 30s default if one
                // of those hosts is slow, and a timeout here would report work the server finished
                // as a failure.
                {timeout: 120_000},
            );

            const report = data as ImportResult;
            setResult(report);

            if (report.created > 0) {
                notify.success(`Imported ${report.created} sponsor${report.created === 1 ? "" : "s"}`);
                await onImported();
            }

            // A clean run has nothing left to read, so get out of the way. Anything skipped or
            // failed stays on screen with its reasons.
            if (report.skipped === 0 && report.failed === 0) {
                reset();
                onClose();
            }
        } catch (err) {
            notify.error(getErrMsg(err, "Failed to import sponsors"));
        } finally {
            setImporting(false);
        }
    };

    const problemRows = result?.rows.filter((r) => r.outcome !== "CREATED") ?? [];

    return (
        <Modal
            open={open}
            onClose={close}
            as="form"
            onSubmit={runImport}
            size="xxl"
            submitting={importing}
            primaryText="Import"
            secondaryText="Cancel"
            onSecondary={close}
            title="Import sponsors from JSON"
            description="Bulk-create sponsors from the organiser's list. Each logo is downloaded and re-hosted on our own storage. Admin only."
        >
            <div className="space-y-4 text-left">
                <div>
                    <label className="block text-sm font-medium text-gray-900">
                        Tier <span className="font-normal text-gray-500">(applied to all imported sponsors)</span>
                    </label>
                    <select
                        value={tier}
                        onChange={(e) => setTier(e.target.value as SponsorTier)}
                        disabled={importing}
                        className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                    >
                        {TIERS.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".json,application/json"
                        className="hidden"
                        onChange={(e) => {
                            pickFile(e.target.files?.[0] ?? null);
                            e.currentTarget.value = "";
                        }}
                    />
                    <Button
                        type="button"
                        variant="secondary"
                        size="md"
                        disabled={importing}
                        onClick={() => fileRef.current?.click()}
                        leftIcon={<UploadIcon className="size-4" aria-hidden="true"/>}
                    >
                        Choose JSON file
                    </Button>
                </div>

                {error ? (
                    <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 ring-1 ring-red-100">
                        {error}
                    </div>
                ) : null}

                {result ? (
                    <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 ring-1 ring-gray-200">
                        <div className="font-medium text-gray-900">
                            {result.created} created, {result.skipped} skipped, {result.failed} failed
                        </div>
                        {problemRows.length ? (
                            <ul className="mt-2 space-y-1 text-xs">
                                {problemRows.map((r) => (
                                    <li key={r.row}>
                                        <span className="font-medium">Row {r.row}</span>
                                        {r.name ? ` (${r.name})` : ""}:{" "}
                                        <span className={r.outcome === "FAILED" ? "text-red-600" : "text-gray-600"}>
                                            {r.message ?? r.outcome}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                    </div>
                ) : null}

                <div>
                    <label className="mb-1 block text-sm font-medium text-gray-900">Sponsors JSON</label>
                    <textarea
                        value={text}
                        onChange={(e) => {
                            setText(e.target.value);
                            setError(null);
                        }}
                        rows={10}
                        spellCheck={false}
                        disabled={importing}
                        placeholder={'[{ "name": "Standard Bank", "website": "https://www.standardbank.com.na/namibia/", "image_url": "https://example.com/logo.png" }]'}
                        className="block w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs text-gray-800 shadow-sm focus:border-orange-600 focus:outline-none focus:ring-1 focus:ring-orange-600"
                    />
                    <p className="mt-1.5 text-xs text-gray-500">
                        Required per row: <span className="font-mono">name</span> and{" "}
                        <span className="font-mono">image_url</span>. Optional:{" "}
                        <span className="font-mono">website</span> (may be <span className="font-mono">null</span>).
                        Logos must be PNG, JPEG or WebP and under 5MB. A sponsor whose name is already on the event
                        is skipped, so re-importing the same file is safe.
                    </p>
                </div>
            </div>
        </Modal>
    );
}
